export const R_LIMITS = {
  daily: -3,
  weekly: -7.5,
  monthly: -10,
}

const HOUR = 60 * 60 * 1000
const DAY = 24 * HOUR

export const BLOCK_DURATIONS = {
  daily: DAY,
  weekly: 7 * DAY,
}

export type RuleKey = 'daily' | 'weekly' | 'monthly'

export interface RiskBlock {
  rule: RuleKey
  value: number
  limit: number
  label: string
  consequence: string
  blockedUntil: Date
}

type ClosedSetup = {
  status: string
  r_value?: number | null
  close_date?: Date | string | null
  updated_at?: string | null
}

// Closed setups carry both updated_at (modification time) and close_date (when the
// trade actually finished). Prefer close_date so that later edits (tags, notes,
// hiding, etc.) do not shift the trade into another time window.
function closedAt(s: ClosedSetup): Date | null {
  if (s.close_date) {
    const d = s.close_date instanceof Date ? s.close_date : new Date(s.close_date)
    if (!isNaN(d.getTime())) return d
  }
  if (s.updated_at) {
    const d = new Date(s.updated_at)
    if (!isNaN(d.getTime())) return d
  }
  return null
}

export function netRSince(closed: ClosedSetup[], since: Date): number {
  return closed.reduce((sum, s) => {
    const at = closedAt(s)
    if (!at || at < since) return sum
    return sum + (s.status === 'success' ? s.r_value || 0 : -(s.r_value || 0))
  }, 0)
}

export function startOfToday(): Date {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

export function startOfThisWeek(): Date {
  const d = startOfToday()
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff)
}

export function startOfThisMonth(): Date {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

// End of the calendar month FOLLOWING the given date (inclusive of the whole next month).
// Example: any moment in May -> last ms of June. Aligns with the "blocked for the following month" rule.
export function endOfNextMonth(from: Date): Date {
  return new Date(from.getFullYear(), from.getMonth() + 2, 1, 0, 0, 0, -1)
}

// Returns the exact moment the cumulative R crossed the limit within the given window.
function findTriggerMoment(
  closed: ClosedSetup[],
  since: Date,
  limit: number,
  now: Date
): Date | null {
  const inWindow = closed
    .map((s) => ({s, at: closedAt(s)}))
    .filter((x): x is {s: ClosedSetup; at: Date} => x.at !== null && x.at >= since && x.at <= now)
    .sort((a, b) => a.at.getTime() - b.at.getTime())

  let cumR = 0
  for (const {s, at} of inWindow) {
    cumR += s.status === 'success' ? s.r_value || 0 : -(s.r_value || 0)
    if (cumR <= limit) return at
  }
  return null
}

export function getRiskBlock(closed: ClosedSetup[], now: Date = new Date()): RiskBlock | null {
  // Monthly: cumulative from start of month, blocked until end of the FOLLOWING calendar month.
  const monthlyMoment = findTriggerMoment(closed, startOfThisMonth(), R_LIMITS.monthly, now)
  if (monthlyMoment) {
    const blockedUntil = endOfNextMonth(monthlyMoment)
    if (now < blockedUntil) {
      return {
        rule: 'monthly',
        value: netRSince(closed, startOfThisMonth()),
        limit: R_LIMITS.monthly,
        label: 'Monthly',
        consequence: 'Cannot trade for the following month',
        blockedUntil,
      }
    }
  }

  // Weekly: cumulative from start of week, blocked for 168 hours from trigger moment
  const weeklyMoment = findTriggerMoment(closed, startOfThisWeek(), R_LIMITS.weekly, now)
  if (weeklyMoment) {
    const blockedUntil = new Date(weeklyMoment.getTime() + BLOCK_DURATIONS.weekly)
    if (now < blockedUntil) {
      return {
        rule: 'weekly',
        value: netRSince(closed, startOfThisWeek()),
        limit: R_LIMITS.weekly,
        label: 'Weekly',
        consequence: 'Cannot trade for 1 week',
        blockedUntil,
      }
    }
  }

  // Daily: cumulative from start of today, blocked for 24 hours from trigger moment
  const dailyMoment = findTriggerMoment(closed, startOfToday(), R_LIMITS.daily, now)
  if (dailyMoment) {
    const blockedUntil = new Date(dailyMoment.getTime() + BLOCK_DURATIONS.daily)
    if (now < blockedUntil) {
      return {
        rule: 'daily',
        value: netRSince(closed, startOfToday()),
        limit: R_LIMITS.daily,
        label: 'Daily',
        consequence: 'Cannot trade for 24 hours',
        blockedUntil,
      }
    }
  }

  return null
}
